// src/services/users.js — profiles, presence, search
import { doc, getDoc, onSnapshot, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { ref, onValue } from 'firebase/database';
import { db, rtdb } from '../config/firebase';

const _profileCache = new Map();

export async function getUserProfile(uid) {
  if (!uid) return null;
  if (_profileCache.has(uid)) return _profileCache.get(uid);
  try {
    const snap = await getDoc(doc(db, 'users', uid));
    const p = snap.exists() ? { uid, ...snap.data() } : null;
    if (p) _profileCache.set(uid, p);
    return p;
  } catch {
    return null;
  }
}

export function watchUserProfile(uid, cb) {
  return onSnapshot(doc(db, 'users', uid), (snap) => {
    if (snap.exists()) { const p = { uid, ...snap.data() }; _profileCache.set(uid, p); cb(p); }
  });
}

/**
 * Realtime presence from RTDB `status/{uid}`.
 *
 * 🟥 THE SHAPE WAS WRONG, which is why the header only ever said "last seen …".
 *    This file assumed `{ state: 'online', last_changed }` — a common Firebase
 *    presence convention, but NOT what the app writes. HomeScreen.tsx:11612 writes
 *        set(statusRef, { online: true,  lastSeen: null })
 *    and its onDisconnect writes
 *        { online: false, lastSeen: <server ts> }
 *    So `state` was always undefined → `state === 'online'` was always false → the
 *    header could never show "online", while `lastSeen` DID exist and rendered fine.
 *    That is exactly the reported symptom.
 *
 * Emits a normalised object so callers don't have to know any of this:
 *   { online: boolean, lastSeen: number|null, state: 'online'|'offline', ...raw }
 * The legacy `state`/`last_changed` form is still accepted in case an older client
 * ever wrote it.
 */
/* ⏱ "ONLINE" IS A LEASE (the app's services/presenceLease — the same numbers). 🟥 A phone whose network was cut
   stayed "online" here for as long as the database took to notice — it sends no close. 🟢 The phone renews
   `at` (the server's clock) every 30 s while the app is in front; "online" is believed only while the last
   renewal is under 75 s old, then it is "last seen" at that renewal — and this watcher says so by itself
   when the lease runs out (no write ever arrives). A node with no `at` (a phone not yet updated, a guest's
   tab) is read as before. */
export const PRESENCE_TTL_MS = 75_000;
let _offset = 0;
let _offsetOn = false;
const serverNow = () => Date.now() + _offset;
export function readPresence(v, now) {
  if (!v) return null;
  const num = (x) => { const n = typeof x === 'number' ? x : Number(x); return Number.isFinite(n) && n > 0 ? n : null; };
  const at = num(v.at);
  const said = v.online === true || v.state === 'online';
  const fresh = said && (at == null || now - at < PRESENCE_TTL_MS);
  const lastSeen = fresh ? num(v.lastSeen ?? v.last_changed ?? v.lastChanged) : (said ? at : (num(v.lastSeen ?? v.last_changed ?? v.lastChanged) ?? at));
  return { ...v, online: fresh, lastSeen, state: fresh ? 'online' : 'offline', until: fresh && at != null ? at + PRESENCE_TTL_MS : null };
}
export function watchPresence(uid, cb) {
  if (!_offsetOn) { _offsetOn = true; try { onValue(ref(rtdb, '.info/serverTimeOffset'), (s) => { const o = Number(s.val()); _offset = Number.isFinite(o) ? o : 0; }); } catch { _offsetOn = false; } }
  let raw = null; let timer = null; let live = true;
  const emit = () => {
    if (!live) return;
    if (timer) { clearTimeout(timer); timer = null; }
    const p = readPresence(raw, serverNow());
    if (p && p.until != null) timer = setTimeout(emit, Math.max(0, p.until - serverNow()) + 250);
    cb(p);
  };
  const off = onValue(ref(rtdb, `status/${uid}`), (snap) => { raw = snap.val(); emit(); });
  return () => { live = false; if (timer) clearTimeout(timer); off(); };
}

/**
 * Find people by name, username, phone or email.
 *
 * 🟢 Firestore has no substring search, so each field is a PREFIX range query
 *    (`>= t` … `<= t + \uf8ff`) and the five run in parallel, de-duped by uid.
 *    username/email are matched lowercased because that's how they're stored;
 *    phone is matched BOTH raw and digits-only, since people type "+1 313 555…"
 *    while the stored value is usually "+13135551234".
 */
export async function searchUsers(text, excludeUid) {
  const t = (text || '').trim();
  if (!t) return [];
  const usersRef = collection(db, 'users');
  const results = new Map();
  const END = '\uf8ff';
  const lower = t.toLowerCase();
  const digits = t.replace(/[^\d+]/g, '');

  const ranges = [
    ['username', lower],
    ['fullName', t],
    ['email', lower],
    ['phone', t],
    ...(digits && digits !== t ? [['phone', digits]] : []),
    ...(digits ? [['phoneNumber', digits]] : []),
  ];

  await Promise.all(ranges.map(async ([field, term]) => {
    if (!term) return;
    try {
      const q = query(usersRef, where(field, '>=', term), where(field, '<=', term + END));
      const snap = await getDocs(q);
      snap.docs.forEach((d) => {
        if (d.id === excludeUid) return;
        const u = d.data();
        /* 🟥 GUESTS ARE NOT IN THE DIRECTORY. A guest-link visitor gets a real
           user doc — same shape as everyone's, so sealing and avatars need no
           special case — which also means a prefix query on their email or the
           name they typed would surface them to strangers, and to any picker
           (new group, lists, start chat). They are reachable through their one
           link and nowhere else. */
        if (u && (u.isGuest === true || u.guestLinkToken)) return;
        /* 💬 AND NEITHER IS THE OFFICIAL ALLO ACCOUNT. Same reasoning, different
           direction: it has a real user doc so avatars and names resolve
           everywhere without a special case, which also means a prefix query on
           "allo" would offer it in the new-group picker, the start-chat sheet,
           the forward list and the share sheet. The only way into that chat is
           Help — every other door opens onto a conversation with a locked
           composer and a state machine nobody told about the message.

           🟥 CHECKED TWO WAYS ON PURPOSE. `hidden` is the flag the server sets
           (ensureOfficialAccount), and the uid is the fact. If a merge ever
           drops the flag, the uid still holds. */
        if (u && (u.hidden === true || d.id === 'allo_official')) return;
        results.set(d.id, { uid: d.id, ...u });
      });
    } catch { /* a missing index on one field must not kill the whole search */ }
  }));

  return Array.from(results.values());
}

/* 💬 "@" IN A VIBE COMMENT — who a name search finds, and nothing else.
   🟥 NARROWER THAN searchUsers ON PURPOSE. That one answers emails and phone
      numbers because starting a chat needs them; a mention picker shows a name
      and a face, and an email that "finds" somebody is a way to learn that an
      address belongs to an account. Username and full name only (as typed and
      Capitalised), the same exclusions — guests and the official account are
      not in the directory — and answers cached per term. */
const _mentionSearchCache = new Map();
export async function searchMentionPeople(term) {
  const t = String(term || '').trim();
  if (!t) return [];
  if (_mentionSearchCache.has(t)) return _mentionSearchCache.get(t);
  const usersRef = collection(db, 'users');
  const END = '';
  const cap = t.charAt(0).toUpperCase() + t.slice(1);
  const ranges = [['username', t.toLowerCase()], ['fullName', cap], ['fullName', t]];
  const out = new Map();
  await Promise.all(ranges.map(async ([field, v]) => {
    try {
      const snap = await getDocs(query(usersRef, where(field, '>=', v), where(field, '<=', v + END), limit(6)));
      snap.docs.forEach((d) => {
        const u = d.data() || {};
        if (u.isGuest === true || u.guestLinkToken || u.hidden === true || d.id === 'allo_official') return;
        const name = u.fullName || u.username || '';
        if (!name || out.has(d.id)) return;
        out.set(d.id, { uid: d.id, name, photo: u.profileImage || '', username: u.username || null });
      });
    } catch { /* one field failing must not kill the rest */ }
  }));
  const list = Array.from(out.values());
  if (_mentionSearchCache.size > 200) _mentionSearchCache.clear();
  _mentionSearchCache.set(t, list);
  return list;
}
